import React, { useMemo } from "react";
import { Check, X } from "lucide-react";

export function scorePassword(pw = "") {
  let score = 0;
  if (!pw) return { score: 0, label: "Empty", color: "bg-[#D2CDBE]", checks: {} };
  const checks = {
    length: pw.length >= 8,
    long: pw.length >= 12,
    upper: /[A-Z]/.test(pw),
    lower: /[a-z]/.test(pw),
    number: /\d/.test(pw),
    symbol: /[^A-Za-z0-9]/.test(pw),
  };
  Object.values(checks).forEach((v) => v && score++);
  // Map 0-6 to 0-4
  let bucket = 0;
  if (score >= 6) bucket = 4;
  else if (score >= 5) bucket = 3;
  else if (score >= 4) bucket = 2;
  else if (score >= 2) bucket = 1;
  const meta = [
    { label: "Too weak", color: "bg-[#992F2F]", text: "text-[#992F2F]" },
    { label: "Weak", color: "bg-[#B58957]", text: "text-[#8A4632]" },
    { label: "Fair", color: "bg-[#B58957]", text: "text-[#8A4632]" },
    { label: "Strong", color: "bg-[#264C39]", text: "text-[#264C39]" },
    { label: "Excellent", color: "bg-[#1B3526]", text: "text-[#1B3526]" },
  ][bucket];
  return { score: bucket, ...meta, checks };
}

export default function PasswordStrength({ value = "" }) {
  const s = useMemo(() => scorePassword(value), [value]);
  const segments = [0, 1, 2, 3];
  if (!value) return null;
  return (
    <div className="mt-2" data-testid="password-strength">
      <div className="flex gap-1.5">
        {segments.map((i) => (
          <div
            key={i}
            className={`h-1.5 flex-1 rounded-full transition-colors ${i < s.score ? s.color : "bg-[#D2CDBE]"}`}
          />
        ))}
      </div>
      <div className="mt-2 flex items-center justify-between text-xs">
        <span className={`font-medium ${s.text}`} data-testid="password-strength-label">{s.label}</span>
        <span className="text-[#4F4D45]">{value.length} chars</span>
      </div>
      <ul className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-[#4F4D45]">
        <Rule ok={s.checks.length} text="8+ characters" />
        <Rule ok={s.checks.long} text="12+ for excellent" />
        <Rule ok={s.checks.upper} text="Uppercase letter" />
        <Rule ok={s.checks.lower} text="Lowercase letter" />
        <Rule ok={s.checks.number} text="A number" />
        <Rule ok={s.checks.symbol} text="A symbol" />
      </ul>
    </div>
  );
}

function Rule({ ok, text }) {
  return (
    <li className={`flex items-center gap-1.5 ${ok ? "text-[#264C39]" : "text-[#4F4D45]"}`}>
      {ok ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
      {text}
    </li>
  );
}
